import { __awaiter } from "tslib";
import { basketMutations, checkoutMutations } from '@as-react/common-queries';
import { useAemConfig } from '@as-react/common-components';
import { PaymentGroupName, PaymentProvider, ErrorMessage } from '@as-react/common-types';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { analyticsActions } from '../redux/analytics/actions/AnalyticsActions.creators';
import LocalStorageUtil from '../util/LocalStorageUtil';
import { CHOSEN_PAYMENT_OPTION } from '../constants/StorageKeysConstants';
import { usePaymentState } from '../context/CheckoutPaymentContext';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
// should only be used inside PaymentContext
export const useSelectPaymentOption = () => {
    const { t } = useTranslation(I18N_NAMESPACES.buy);
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const [, dispatch] = usePaymentState();
    const reduxDispatch = useDispatch();
    const { postPaymentOptionId, postPaymentProviderInput } = checkoutMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { postSavePoint } = basketMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { mutateAsync: mutatePostPaymentOptionId, isPending: postPaymentOptionPending } = useMutation(postPaymentOptionId);
    const { mutateAsync: mutatePostSavePoint } = useMutation(postSavePoint);
    const { mutateAsync: mutatePostPaymentProviderInput } = useMutation(postPaymentProviderInput);
    const selectPaymentGroup = (group) => __awaiter(void 0, void 0, void 0, function* () {
        const options = group.paymentOptions;
        const isSingleOption = options.length === 1;
        let optionId;
        let provider;
        let optionName;
        try {
            dispatch({ type: 'set_payment_group_success', payload: false });
            if (isSingleOption || group.name === PaymentGroupName.ECOCHEQUE) {
                optionId = options[0].paymentOptionId;
                provider = options[0].provider;
                optionName = options[0].name;
            }
            else {
                const findOption = options.find(opt => opt.name === group.name);
                optionId = findOption === null || findOption === void 0 ? void 0 : findOption.paymentOptionId;
                provider = findOption === null || findOption === void 0 ? void 0 : findOption.provider;
                optionName = findOption === null || findOption === void 0 ? void 0 : findOption.name;
                if (group.supportsTokenization) {
                    yield mutatePostSavePoint();
                    reduxDispatch(analyticsActions.paymentStepOption(group.name));
                }
            }
            if (optionId && provider) {
                yield mutatePostPaymentOptionId(optionId);
                yield mutatePostSavePoint();
                dispatch({ type: 'set_payment_group_success', payload: true });
                dispatch({ type: 'set_selected_payment_group', payload: group });
                if (provider === PaymentProvider.KLARNA) {
                    const { clientToken } = yield mutatePostPaymentProviderInput(provider);
                    dispatch({ type: 'set_payment_klarna_token', payload: clientToken });
                }
            }
            else {
                dispatch({ type: 'set_selected_payment_group', payload: group });
            }
            LocalStorageUtil.setItem(CHOSEN_PAYMENT_OPTION, group.name.replace(/\s+/g, ''));
            dispatch({ type: 'set_payment_option_error', payload: { [group.name]: undefined } });
            if (optionName) {
                reduxDispatch(analyticsActions.paymentStepOption(optionName));
            }
            return true;
        }
        catch (e) {
            dispatch({ type: 'set_payment_option_error', payload: { [group.name]: t(ErrorMessage.GENERAL_PAYMENT) } });
            return false;
        }
    });
    return { selectPaymentGroup, postPaymentOptionPending };
};
